import React, { useState, useEffect } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { PlayerAvatar, PlayerName, bannerStyle } from "./PlayerIdentity";
import Heart from "./Heart";
import { t } from "../i18n";
import { useSounds, useCountdownTick } from "../sound";
import { alertDialog } from "../dialog";

// Battle Royale: 4-choice questions, a pool of lives per player, and the last
// one standing wins. Server-paced, like the Culture quiz.
function BattleRoyale({ gameId, player }) {
  const [index, setIndex]         = useState(0);
  const [total, setTotal]         = useState(0);
  const [question, setQuestion]   = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [duration, setDuration]   = useState(15000);
  const [players, setPlayers]     = useState([]);
  const [myChoice, setMyChoice]   = useState(null);
  const [alive, setAlive]         = useState(true);
  const [reveal, setReveal]       = useState(null);   // end-of-question result
  const [now, setNow]             = useState(Date.now());

  const myId = player?.playerId;
  const { playSuccess, playFail } = useSounds();

  useEffect(() => {
    const applyState = (s) => {
      setIndex(s.index); setTotal(s.total); setQuestion(s.question);
      setStartedAt(s.startedAt); setDuration(s.duration);
      setPlayers(s.players); setMyChoice(s.myChoice); setAlive(s.alive);
      setReveal(null);
    };
    socket.on("brState", applyState);
    socket.on("brQuestion", applyState);
    socket.on("brAnswerAck", ({ choice }) => setMyChoice(choice));
    socket.on("brPlayers", ({ players }) => setPlayers(players));
    socket.on("brRoundEnd", (r) => {
      setReveal(r); setPlayers(r.players); setStartedAt(null);
      const mine = r.results.find(x => x.playerId === myId);
      if (mine) (mine.correct ? playSuccess : playFail)();
      if (mine && mine.lives <= 0) setAlive(false);
    });
    socket.on("brError", ({ error }) => alertDialog(error));

    socket.emit("brGetState", { gameId });
    return () => {
      ["brState", "brQuestion", "brAnswerAck", "brPlayers", "brRoundEnd", "brError"]
        .forEach(e => socket.off(e));
    };
  }, [gameId, myId, playSuccess, playFail]);

  useEffect(() => {
    if (!startedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [startedAt]);

  const pick = (i) => {
    if (!alive || myChoice !== null || !startedAt || reveal) return;
    socket.emit("brAnswer", { gameId, index, choice: i });
  };

  const remaining = startedAt ? Math.max(0, Math.ceil((duration - (now - startedAt)) / 1000)) : 0;
  const aliveCount = players.filter(p => !p.eliminated).length;

  useCountdownTick(remaining, { active: !!startedAt && !reveal });
  useGameTimer(startedAt ? { remaining, duration, idle: !!reveal } : null);

  const myResult = reveal?.results.find(r => r.playerId === myId);

  if (!question) {
    return <div className="BattleRoyale"><p className="br-waiting">{t("battleRoyale.waiting")}</p></div>;
  }

  return (
    <div className={`BattleRoyale${alive ? "" : " is-out"}`}>
      <div className="br-header">
        <div className="br-header-info">
          <span className="br-survivors">{t("battleRoyale.survivors", { n: aliveCount })}</span>
          <p className="br-progress">{t("battleRoyale.questionOf", { index: index + 1, total })}</p>
        </div>
      </div>

      <div className="br-layout">
        <main className="br-main">
          {question.media_url && (
            /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
              ? <img src={question.media_url} alt="" className="quiz-media" />
              : <audio controls src={question.media_url} />
          )}
          <h3 className="br-question">{question.question}</h3>

          <div className="br-choices">
            {(question.answers || []).map((a, i) => {
              const isCorrect = reveal && i === reveal.correctAnswer;
              const isMyWrong = reveal && myChoice === i && i !== reveal.correctAnswer;
              return (
                <button key={i}
                  className={`br-choice${myChoice === i ? " picked" : ""}${isCorrect ? " correct" : ""}${isMyWrong ? " wrong" : ""}`}
                  disabled={!alive || myChoice !== null || !startedAt || !!reveal}
                  onClick={() => pick(i)}>
                  <span className="br-choice-key">{"ABCD"[i]}</span>
                  <span className="br-choice-text">{a}</span>
                </button>
              );
            })}
          </div>

          {reveal ? (
            <div className="br-reveal">
              <p className="br-reveal-answer">
                {t("battleRoyale.correctAnswer", { answer: reveal.correctText })}
              </p>
              {alive && myResult && (
                <p className={myResult.correct ? "br-reveal-good" : "br-reveal-bad"}>
                  {myResult.correct ? t("battleRoyale.youSurvived") : t("battleRoyale.youLostLife")}
                </p>
              )}
              {reveal.eliminated.length > 0 && (
                <div className="br-reveal-out">
                  {reveal.eliminated.map(pid => {
                    const p = players.find(x => x.playerId === pid);
                    return <span key={pid}>{t("battleRoyale.knockedOut", { pseudo: p?.pseudo || "?" })}</span>;
                  })}
                </div>
              )}
            </div>
          ) : !alive ? (
            <p className="br-spectator">{t("battleRoyale.spectator")}</p>
          ) : myChoice !== null ? (
            <p className="br-waiting">{t("battleRoyale.waitingOthers")}</p>
          ) : null}
        </main>

        <aside className="br-players">
          {players.map(p => (
            <div key={p.playerId} style={bannerStyle(p)}
              className={`br-player${p.playerId === myId ? " me" : ""}${p.eliminated ? " out" : ""}${p.offline ? " offline" : ""}`}>
              <PlayerAvatar player={p} size={30} />
              <PlayerName player={p} className="br-player-name" />
              {p.eliminated ? (
                <span className="br-player-out">{t("battleRoyale.eliminated")}</span>
              ) : (
                <span className="br-hearts" title={t("battleRoyale.lives")}>
                  {Array.from({ length: p.lives }, (_, i) => <Heart key={i} className="br-heart" />)}
                  {p.answered && !reveal && <span className="br-answered">✓</span>}
                </span>
              )}
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}

export default BattleRoyale;
